import { useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { formatNumberAndCents, kebabCase } from '@makemydeal/dr-common-utils';
import * as dealScreenErrorActionCreators from '@makemydeal/dr-dash-store/actions/dealScreenErrorActionCreators';
import * as interactionAnalyticsActionCreators from '@makemydeal/dr-dash-store/actions/interactionAnalyticsActionCreators';
import * as offerReduxActionCreators from '@makemydeal/dr-dash-store/actions/offerRedux';
import * as offerActionTypes from '@makemydeal/dr-dash-store/actionTypes/offerActionTypes';
import * as deskingSelectors from '@makemydeal/dr-dash-store/selectors/deskingSelectors';
import * as offerSelectors from '@makemydeal/dr-dash-store/selectors/offerSelectors';
import { featureToggleSelectors } from '@makemydeal/dr-shared-store';
import type { DealTabs } from '@makemydeal/dr-shared-types';

import { GENERIC_LAST_FIELD_CHANGED_ERROR, SECURITY_DEPOSIT } from '../../../constants';
import { dealLockHooks } from '../../../hooks';
import { useFieldOverrideIndicator } from '../../../hooks/useFieldOverrideIndicator';
import { usePaymentCall } from '../../../utils/usePaymentCall';

export const useSecurityDepositField = ({ tab }: { tab?: DealTabs }) => {
    const isDealScreenRedDotErrorsEnabled = useSelector(featureToggleSelectors.isDealScreenRedDotErrorsEnabled);
    const dispatch = useDispatch();
    const isLocked = dealLockHooks.useIsDealLocked();
    const { recordManualOverride } = useFieldOverrideIndicator('securityDeposit');
    const {
        value: depositValue,
        handleChange: handleDepositeChanged,
        handleBlur: handleDepositBlurred,
        handleKeyDown
    } = usePaymentCall(
        offerSelectors.getSecurityDepositOverrideWithFallback,
        offerReduxActionCreators.updateSecurityDepositAmountOverride,
        { formatValueToString: (amount: any) => formatNumberAndCents(amount), onUserEdit: recordManualOverride }
    );

    const fieldName = kebabCase(SECURITY_DEPOSIT);
    const failed = useSelector(deskingSelectors.wasTheLastFailure(offerActionTypes.UPDATED_SECURITY_DEPOSIT_OVERRIDE));
    const failedMessage = useMemo(() => (failed ? GENERIC_LAST_FIELD_CHANGED_ERROR : undefined), [failed]);

    useEffect(() => {
        if (isDealScreenRedDotErrorsEnabled && tab) {
            dispatch(dealScreenErrorActionCreators.setFieldError(tab, fieldName, failed));
        }
    }, [isDealScreenRedDotErrorsEnabled, dispatch, failed, tab]);

    return {
        label: SECURITY_DEPOSIT,
        inputProps: {
            name: fieldName,
            onChange: handleDepositeChanged,
            onBlur: (e: any) => {
                handleDepositBlurred(e);
                dispatch(interactionAnalyticsActionCreators.securityDepositTextInput());
            },
            placeholder: '0.00',
            hasError: failed,
            errorMessage: failedMessage,
            value: depositValue,
            'data-testid': fieldName,
            allowDecimal: true,
            decimalMaxLength: 2,
            decimalMinLength: 0,
            disabled: isLocked,
            inputPrefix: '$',
            autoInsertCommas: true,
            size: 'sm' as const,
            onKeyDown: handleKeyDown
        }
    };
};
